import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router-dom';
import { ErrorNotice, FactList, Heading, PageHeading, Panel, SkeletonTable, useReturnTo } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatMoney } from '@/lib/cash-flow';
import { SALES_ACCOUNTS_PATH, placeOf, readSalesAccount } from '@/lib/sales-data';
import { formatDay, isNotAllowed } from '@/lib/sales-workspace';
import { AccountContactsTable, AccountLink, DealTable, EngagementTimeline } from '@/components/sales-workspace/sales-records';

/** A website as typed in Salesforce ("acme.com"), made openable. */
function websiteHref(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}

function Section({ title, children }: { readonly title: string; readonly children: React.ReactNode }): React.ReactElement {
  return (
    <div className="mt-6">
      <Heading variant="card" as="h2" className="mb-3">
        {title}
      </Heading>
      {children}
    </div>
  );
}

/**
 * One account for a rep: who it is, what is open, what it booked before,
 * the deals that did not book, its people and every recent touch.
 *
 * Dark: the API answers 403 to anyone off the sales workspace allow-list.
 *
 * @returns The page.
 */
export function SalesAccountPage(): React.ReactElement {
  const { accountId = '' } = useParams();
  const backTo = useReturnTo(SALES_ACCOUNTS_PATH);
  const detail = useQuery({
    queryKey: ['sales-account', accountId],
    queryFn: ({ signal }) => readSalesAccount(accountId, signal),
    retry: (count, error) => !isNotAllowed(error) && count < 2,
  });

  if (detail.isError && isNotAllowed(detail.error)) return <Navigate to="/events" replace />;
  if (detail.isPending) {
    return (
      <section className="animate-portal-rise">
        <SkeletonTable rows={6} columns={['w-[30%]', 'w-[50%]']} label="Loading the account…" />
      </section>
    );
  }
  if (detail.isError) {
    return (
      <section className="animate-portal-rise">
        <PageHeading eyebrow="SALES" backTo={backTo} title="Account" />
        <ErrorNotice title="Account unavailable" message={errorMessage(detail.error)} />
      </section>
    );
  }

  const { account, openDeals, pastBookings, otherClosedDeals, contacts, timeline } = detail.data;
  const bookedValue = pastBookings.reduce((sum, deal) => sum + Math.max(0, deal.totalCost ?? 0), 0);
  const lastBooking = pastBookings[0];

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        backTo={backTo}
        title={account.name || 'Untitled account'}
        subtitle={[account.type, account.industry, account.ownerName && `Owner: ${account.ownerName}`].filter(Boolean).join(' · ')}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel className="p-5 lg:col-span-2">
          <Heading variant="card" as="h2" className="mb-3">
            About
          </Heading>
          <FactList
            columns={3}
            facts={[
              { label: 'Location', value: placeOf(account.billingCity, account.billingState) || '—' },
              { label: 'Phone', value: account.phone ? <a href={`tel:${account.phone}`} className="text-brand hover:underline">{account.phone}</a> : '—' },
              {
                label: 'Website',
                value: account.website ? (
                  <a href={websiteHref(account.website)} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                    {account.website}
                  </a>
                ) : (
                  '—'
                ),
              },
              { label: 'Parent account', value: account.parentId ? <AccountLink id={account.parentId} name={account.parentName} /> : '—' },
              { label: 'Owner', value: account.ownerName || '—' },
              { label: 'Last activity', value: formatDay(account.lastActivityDate, { withYear: true }) },
            ]}
          />
        </Panel>
        <Panel className="p-5">
          <Heading variant="card" as="h2" className="mb-3">
            At a glance
          </Heading>
          <FactList
            columns={2}
            facts={[
              { label: 'Open deals', value: openDeals.length },
              { label: 'Bookings', value: pastBookings.length },
              { label: 'Booked value', value: bookedValue > 0 ? formatMoney(bookedValue) : '—' },
              { label: 'Last booking', value: formatDay(lastBooking?.eventDate ?? lastBooking?.signedDate ?? null, { withYear: true }) },
            ]}
          />
        </Panel>
      </div>

      <Section title="Open deals">
        <DealTable
          deals={openDeals}
          prefix="open"
          defaultSort={{ key: 'eventDate', direction: 'asc' }}
          emptyTitle="No open deals"
          emptyMessage="Deals still being worked show here."
        />
      </Section>
      <Section title="Past bookings">
        <DealTable
          deals={pastBookings}
          prefix="booked"
          defaultSort={{ key: 'eventDate', direction: 'desc' }}
          emptyTitle="Nothing booked yet"
          emptyMessage="Signed deals show here with their program, event date and value."
        />
      </Section>
      <Section title="Other closed deals">
        <DealTable
          deals={otherClosedDeals}
          prefix="closed"
          defaultSort={{ key: 'eventDate', direction: 'desc' }}
          emptyTitle="No other closed deals"
          emptyMessage="Deals that closed without booking (lost or canceled) show here."
        />
      </Section>
      <Section title="Contacts">
        <AccountContactsTable contacts={contacts} prefix="people" />
      </Section>
      <Section title="Engagement">
        <Panel className="p-5">
          <EngagementTimeline items={timeline} emptyMessage="Emails, calls and meetings on this account's deals show here." />
        </Panel>
      </Section>
    </section>
  );
}
